import type { ReactNode } from "react";
import { genreOptions, regionOptions, typeOptions, yearOptions } from "@/mocks/movies";
import { toggleInArray, type FilterState } from "@/utils/movieQuery";

interface FilterPanelProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  onReset: () => void;
}

interface FilterSectionProps {
  title: string;
  icon: string;
  children: ReactNode;
}

function FilterSection({ title, icon, children }: FilterSectionProps) {
  return (
    <div className="border-b border-background-200/70 pb-4 last:border-b-0 last:pb-0">
      <h3 className="mb-3 flex items-center gap-1.5 text-sm font-bold text-foreground-900">
        <i className={`${icon} text-primary-500 leading-none`}></i>
        {title}
      </h3>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

interface ChipProps {
  active: boolean;
  label: string;
  onClick: () => void;
}

function Chip({ active, label, onClick }: ChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
        active
          ? "border-primary-500 bg-primary-500 text-background-50"
          : "border-background-200/70 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
      }`}
    >
      {label}
    </button>
  );
}

export default function FilterPanel({ filters, onChange, onReset }: FilterPanelProps) {
  const activeCount =
    filters.types.length + filters.genres.length + filters.regions.length + filters.years.length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 font-heading text-base font-bold text-foreground-950">
          <i className="ri-filter-3-line text-primary-500 leading-none"></i>
          筛选条件
          {activeCount > 0 && (
            <span className="ml-1 rounded-full bg-accent-500 px-2 py-0.5 text-xs font-bold text-primary-950">
              {activeCount}
            </span>
          )}
        </h2>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-foreground-500 hover:text-primary-600"
        >
          <i className="ri-refresh-line leading-none"></i>
          重置
        </button>
      </div>

      <FilterSection title="作品类型" icon="ri-movie-line">
        {typeOptions.map((t) => (
          <Chip
            key={t}
            label={t}
            active={filters.types.includes(t)}
            onClick={() => onChange({ ...filters, types: toggleInArray(filters.types, t) })}
          />
        ))}
      </FilterSection>

      <FilterSection title="题材类型" icon="ri-price-tag-3-line">
        {genreOptions.map((g) => (
          <Chip
            key={g}
            label={g}
            active={filters.genres.includes(g)}
            onClick={() => onChange({ ...filters, genres: toggleInArray(filters.genres, g) })}
          />
        ))}
      </FilterSection>

      <FilterSection title="地区" icon="ri-earth-line">
        {regionOptions.map((r) => (
          <Chip
            key={r}
            label={r}
            active={filters.regions.includes(r)}
            onClick={() => onChange({ ...filters, regions: toggleInArray(filters.regions, r) })}
          />
        ))}
      </FilterSection>

      <FilterSection title="年份" icon="ri-calendar-line">
        {yearOptions.map((y) => (
          <Chip
            key={y}
            label={`${y}`}
            active={filters.years.includes(y)}
            onClick={() => onChange({ ...filters, years: toggleInArray(filters.years, y) })}
          />
        ))}
      </FilterSection>
    </div>
  );
}